import { useEffect, useState } from 'react';
import { mensajeDeError, useSinConexion } from '@/core/red/conectividad';
import { NecesitaConexion } from '@/ui/NecesitaConexion';
import { useOrg } from '@/features/team/OrgProvider';
import {
  fetchOrganizationSettings,
  MOBILITY_LABELS,
  type MobilityMode,
  type OrganizationSettings,
} from '@/features/clients/clients';
import { TeamPage } from '@/features/team/TeamPage';
import { MigrationCard } from '@/features/migration/MigrationCard';
import { PlantillasCard } from './PlantillasCard';
import { formatCents, parsePesosToCents, updateMobilitySettings } from './catalog';

const MODES: MobilityMode[] = ['none', 'per_km_fuel', 'per_km_fixed', 'flat', 'manual'];

/**
 * Configuración de la organización: repartos habituales, movilidad y equipo.
 *
 * El tarifario y los precios ya no están acá: tienen pantalla propia porque se
 * consultan seguido y estar al final de una lista larga costaba scroll todos
 * los días.
 *
 * La restricción real la aplica RLS: `organization_services`,
 * `reference_prices` y `organization_settings` solo admiten escritura de un
 * administrador. Ocultar controles es ergonomía, no seguridad.
 */
export function ConfigPage() {
  const { activeOrg } = useOrg();
  const orgId = activeOrg?.organization_id;
  const isAdmin = activeOrg?.is_admin ?? false;

  const [settings, setSettings] = useState<OrganizationSettings | null>(null);
  const [mode, setMode] = useState<MobilityMode>('per_km_fuel');
  const [charge, setCharge] = useState(true);
  const [rate, setRate] = useState('');
  const [flat, setFlat] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [ok, setOk] = useState(false);
  const [recarga, setRecarga] = useState(0);

  useEffect(() => {
    if (!orgId) return;
    let cancelado = false;
    void (async () => {
      try {
        const config = await fetchOrganizationSettings(orgId);
        if (cancelado || !config) return;
        setSettings(config);
        setMode(config.default_mobility_mode);
        setCharge(config.charge_mobility_by_default);
        setRate(
          config.default_mobility_rate_cents
            ? String(config.default_mobility_rate_cents / 100)
            : '',
        );
        setFlat(
          config.default_flat_mobility_cents
            ? String(config.default_flat_mobility_cents / 100)
            : '',
        );
      } catch (e) {
        if (!cancelado) setError(mensajeDeError(e));
      }
    })();
    return () => {
      cancelado = true;
    };
  }, [orgId, recarga]);

  // Configuración de la organización: se guarda contra el servidor y afecta a
  // todo el equipo. Con la copia del teléfono se podría MOSTRAR, pero no
  // guardar, y una pantalla de configuración que no guarda es una trampa.
  if (useSinConexion()) {
    return (
      <div className="flex flex-col gap-5 p-4">
        <NecesitaConexion
          que="La configuración de la organización se guarda en el servidor y la ven todos."
          testId="config-sin-conexion"
        />
      </div>
    );
  }

  if (!activeOrg || !orgId) return null;

  const guardarMovilidad = async () => {
    setError(null);
    setOk(false);
    try {
      await updateMobilitySettings(orgId, {
        default_mobility_mode: mode,
        charge_mobility_by_default: charge,
        default_mobility_rate_cents: parsePesosToCents(rate),
        default_flat_mobility_cents: parsePesosToCents(flat),
      });
      setOk(true);
      setRecarga((n) => n + 1);
    } catch (e) {
      setError(mensajeDeError(e));
    }
  };

  return (
    <div className="flex flex-col gap-4 p-4" data-testid="config-page">
      {/* Honorarios y Precios se mudaron a pantalla propia: acá adentro
          obligaban a recorrer todo para llegar a lo que más se toca. */}
      <PlantillasCard organizationId={orgId} isAdmin={isAdmin} />

      <section className="superficie p-4" data-testid="mobility-card">
        <h2 className="mb-1 font-medium">Movilidad</h2>
        <p className="mb-3 text-sm text-[var(--tinta-suave)]">
          Es el valor por defecto de la organización. Cada establecimiento y cada trabajo pueden
          cambiarlo.
        </p>

        {error && (
          <p role="alert" className="mb-2 text-sm text-[var(--peligro)]">
            {error}
          </p>
        )}
        {ok && <p className="mb-2 text-sm text-[var(--exito)]">Guardado</p>}

        {isAdmin ? (
          <div className="flex flex-col gap-3">
            <label className="flex flex-col gap-1 text-sm">
              ¿Se cobra movilidad por defecto?
              <select
                value={charge ? 'yes' : 'no'}
                onChange={(e) => setCharge(e.target.value === 'yes')}
                className="min-h-11 rounded-[var(--radius-control)] border border-[var(--linea-fuerte)] px-2"
                data-testid="mob-charge"
              >
                <option value="yes">Sí</option>
                <option value="no">No</option>
              </select>
            </label>

            <label className="flex flex-col gap-1 text-sm">
              Método
              <select
                value={mode}
                onChange={(e) => setMode(e.target.value as MobilityMode)}
                className="min-h-11 rounded-[var(--radius-control)] border border-[var(--linea-fuerte)] px-2"
                data-testid="mob-mode"
              >
                {MODES.map((m) => (
                  <option key={m} value={m}>
                    {MOBILITY_LABELS[m]}
                  </option>
                ))}
              </select>
            </label>

            {mode === 'per_km_fixed' && (
              <label className="flex flex-col gap-1 text-sm">
                Tarifa por kilómetro
                <input
                  inputMode="decimal"
                  value={rate}
                  onChange={(e) => setRate(e.target.value)}
                  className="min-h-11 rounded-[var(--radius-control)] border border-[var(--linea-fuerte)] px-3"
                  data-testid="mob-rate"
                />
              </label>
            )}

            {mode === 'flat' && (
              <label className="flex flex-col gap-1 text-sm">
                Monto fijo por trabajo
                <input
                  inputMode="decimal"
                  value={flat}
                  onChange={(e) => setFlat(e.target.value)}
                  className="min-h-11 rounded-[var(--radius-control)] border border-[var(--linea-fuerte)] px-3"
                  data-testid="mob-flat"
                />
              </label>
            )}

            {mode === 'per_km_fuel' && (
              <p className="text-xs text-[var(--tinta-tenue)]">
                Se calcula <strong>km × precio del combustible</strong>, tomando el precio de
                referencia del mes de facturación.
              </p>
            )}

            <button
              onClick={() => void guardarMovilidad()}
              className="min-h-11 rounded-[var(--radius-control)] bg-[var(--accion)] px-4 text-sm text-[var(--sobre-accion)]"
              data-testid="mob-save"
            >
              Guardar movilidad
            </button>
          </div>
        ) : (
          <dl className="flex flex-col gap-1 text-sm" data-testid="mobility-readonly">
            <div className="flex justify-between">
              <dt className="opacity-70">Se cobra</dt>
              <dd>{settings?.charge_mobility_by_default ? 'Sí' : 'No'}</dd>
            </div>
            <div className="flex justify-between">
              <dt className="opacity-70">Método</dt>
              <dd>{settings ? MOBILITY_LABELS[settings.default_mobility_mode] : '—'}</dd>
            </div>
            <div className="flex justify-between">
              <dt className="opacity-70">Tarifa por km</dt>
              <dd>{formatCents(settings?.default_mobility_rate_cents ?? null)}</dd>
            </div>
            <p className="mt-2 text-xs text-[var(--tinta-tenue)]">
              Solo un administrador puede modificar la configuración económica.
            </p>
          </dl>
        )}
      </section>

      <TeamPage />

      {/* Sólo aparece si hay algo importado del sistema anterior. */}
      <MigrationCard organizationId={orgId} isAdmin={isAdmin} />
    </div>
  );
}
